import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ConversationCommentBox, type ConversationCommentBoxProps} from '@github-ui/diff-lines/conversation-comment-box'
import {updateUrlHash} from '@github-ui/history'
import {MaximizeIcon, StopIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Dialog, Flash, Heading, IconButton, Spinner, Stack} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import type {AllowedNonCommentReviewType, PullRequest} from '../../page-data/payloads/toolbar'
import {isPullRequestOpenState} from '../../utils/pull-request-state'
import {useSubmitReviewMutation, ReviewEvent} from '../../mutations/use-submit-review-mutation'
import type {ReviewResponse} from '../../page-data/payloads/review-response'
import {useAbandonReviewMutation} from '../../mutations/use-abandon-review-mutation'
import {usePersistedReview} from '../../hooks/use-persisted-review-data'
import styles from './ReviewMenuButton.module.css'
import {
  useGetPendingCommentsFromStore,
  useGetPendingCommentsFromStoreWithoutDiffEntries,
} from '../../hooks/use-generate-pending-comments'
import {usePendingReviews, usePullRequestBasePath} from '../../stores/selectors'
import {KeybindingHint} from '@primer/react/experimental'
import {ReviewTypeSelector} from './review-menu/ReviewTypeSelector'
import {ReviewCommentsSection} from './review-menu/ReviewCommentsSection'
import {OverlayPortalContainer} from './OverlayPortalContainer'
import {useReviewMenuData} from '../../page-data/loaders/use-review-menu-data'

const REVIEW_MENU_PORTAL_ROOT = 'review-menu-portal-root'

/** Pull request object without deferred data fields. If these fields aren't present, the component will fetch its own data. */
type UnenrichedPullRequest = Omit<
  PullRequest,
  'comparison' | 'repository' | 'id' | 'viewerIsCopilotAttributed' | 'viewerHasViolatedPushPolicy'
>

const isEnrichedPullRequest = (pr: PullRequest | UnenrichedPullRequest): pr is PullRequest =>
  'viewerIsCopilotAttributed' in pr &&
  pr.viewerIsCopilotAttributed !== undefined &&
  'viewerHasViolatedPushPolicy' in pr &&
  pr.viewerHasViolatedPushPolicy !== undefined

export interface ReviewMenuButtonProps {
  currentUserLogin: string
  pullRequest: PullRequest | UnenrichedPullRequest
  headRefOid: string
  repository: {id?: number; viewerPermission?: string}
  redirectOnMutation?: boolean
  /** Controls where the DOM for the overlay renders (into the header, or at the global default portal root). */
  overlayPortal?: 'internal' | 'external'
  /** Base branch name from stacked PRs, used to clarify the approve sub-label. */
  stackBaseBranch?: string
}

function getReviewMenuButtonDisplayState({
  viewerIsAuthor,
  viewerAllowedNonCommentReviewTypes,
  isPROpen,
}: {
  viewerIsAuthor: boolean
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  isPROpen: boolean
}): {isHidden: boolean; text?: string} {
  const viewerCanApprove = viewerAllowedNonCommentReviewTypes.includes('APPROVE')
  const viewerCanRequestChanges = viewerAllowedNonCommentReviewTypes.includes('REQUEST_CHANGES')

  switch (true) {
    case !isPROpen:
      return {
        isHidden: false,
        text: 'Comments',
      }
    case viewerIsAuthor:
    case viewerCanApprove:
    case viewerCanRequestChanges:
      return {
        isHidden: false,
        text: 'Review',
      }
    default:
      return {
        isHidden: false,
        text: 'Comments',
      }
  }
}

const SCROLL_TO_ELEMENT_TIMEOUT_MS = 10_000

/**
 * Scrolls to and focuses a DOM element by ID, waiting for it to appear if it's not yet in the DOM.
 * The review timeline item arrives via live update and may not exist immediately.
 */
function scrollToElementWhenAvailable(elementId: string) {
  const existing = document.getElementById(elementId)
  if (existing) {
    focusAndScrollTo(existing)
    return
  }

  const timeoutId = setTimeout(() => observer.disconnect(), SCROLL_TO_ELEMENT_TIMEOUT_MS)

  const observer = new MutationObserver(() => {
    const el = document.getElementById(elementId)
    if (el) {
      clearTimeout(timeoutId)
      observer.disconnect()
      focusAndScrollTo(el)
    }
  })

  observer.observe(document.body, {childList: true, subtree: true})
}

function focusAndScrollTo(el: HTMLElement) {
  // Ensure the element is focusable — timeline items are typically non-interactive
  // containers, so we need tabIndex to allow programmatic focus.
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1')
  }
  el.focus()
  el.scrollIntoView()
}

const defaultDeferredData = {
  viewerHasViolatedPushPolicy: false,
  viewerIsCopilotAttributed: false,
  viewerPermission: 'WRITE',
}

export function ReviewMenuButton({
  currentUserLogin,
  pullRequest,
  headRefOid,
  repository,
  redirectOnMutation = true,
  overlayPortal = 'internal',
  stackBaseBranch,
}: ReviewMenuButtonProps) {
  const pathName = usePullRequestBasePath()

  const {author, state, viewerAllowedNonCommentReviewTypes} = pullRequest

  const pendingCommentData = usePendingReviews()

  const deferredDataEnabled = !isEnrichedPullRequest(pullRequest)

  const {data: deferredData = defaultDeferredData, isLoading: isReviewMenuLoading} = useReviewMenuData(
    pendingCommentData.pendingReviewIDs,
    {enabled: deferredDataEnabled},
  )

  const {viewerHasViolatedPushPolicy, viewerIsCopilotAttributed} = deferredDataEnabled ? deferredData : pullRequest
  const {viewerPermission} = deferredDataEnabled ? deferredData : repository

  const viewerCanWriteToRepo = viewerPermission === 'WRITE' || viewerPermission === 'ADMIN'

  const {persistedReview, persistReviewToStorage, removePersistedReviewFromStorage} = usePersistedReview(pathName)

  const totalPendingComments = pendingCommentData.pendingReviewIDs?.length ?? 0

  // We will probably want to use CurrentUserContext and useCurrentUser
  const viewerIsAuthor = author?.login === currentUserLogin
  const isPROpen = isPullRequestOpenState(state)
  const [openReviewMenu, setOpenReviewMenu] = useState<'dialog' | 'anchored' | 'none'>('none')
  const [reviewSubmissionState, setReviewSubmissionState] = useState<'idle' | 'submitting' | 'canceling'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [reviewEvent, setReviewEvent] = useState<ReviewEvent>(persistedReview?.event ?? ReviewEvent.comment)

  const hasPendingReviews = (pendingCommentData?.pendingReviewIDs ?? []).length > 0
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  // ref and state to keep track of the review body without causing re-renders
  // we ultimately only care about the final value when submitting the review and validating review body presence
  const initialReviewBody = persistedReview?.text ?? ''
  const reviewBodyRef = useRef<string>(initialReviewBody)
  const [hasReviewBody, setHasReviewBody] = useState<boolean>(!!persistedReview?.text)

  const submitDisabled =
    isReviewMenuLoading ||
    reviewSubmissionState === 'submitting' ||
    (!hasReviewBody && reviewEvent === ReviewEvent.comment && !totalPendingComments)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const onReviewBodyChange = useCallback(
    (body: string) => {
      reviewBodyRef.current = body
      setHasReviewBody(!!body.trim())
      persistReviewToStorage(reviewEvent, body)
    },
    [persistReviewToStorage, reviewEvent, setHasReviewBody],
  )

  const handleReviewEventChange = useCallback(
    (newReviewEvent: string | null) => {
      if (Object.values(ReviewEvent).includes(newReviewEvent as ReviewEvent)) {
        setReviewEvent(newReviewEvent as ReviewEvent)
        persistReviewToStorage(newReviewEvent as ReviewEvent, reviewBodyRef.current)
      }
    },
    [persistReviewToStorage, setReviewEvent],
  )

  const handleInlineSuccess = useCallback(
    (redirectUrl: string) => {
      setOpenReviewMenu('none')

      // The component stays mounted so we must reset all transient state back to idle.
      setReviewSubmissionState('idle')
      setErrorMessage(undefined)
      setReviewEvent(ReviewEvent.comment)
      setHasReviewBody(false)
      reviewBodyRef.current = ''

      // Scroll/focus the new review timeline item. The item may not be in
      // the DOM yet since it arrives via live update.
      try {
        const hash = new URL(redirectUrl, window.location.origin).hash
        if (hash) {
          updateUrlHash(hash)
          scrollToElementWhenAvailable(hash.slice(1))
        }
      } catch {
        // Malformed redirectUrl — skip hash update and scroll
      }
    },
    [setOpenReviewMenu, setReviewSubmissionState, setErrorMessage, setReviewEvent, setHasReviewBody],
  )

  const {mutate: submitReview} = useSubmitReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()

      if (redirectOnMutation) {
        window.location.href = redirectUrl
      } else {
        handleInlineSuccess(redirectUrl)
      }
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const {mutate: abandonReview} = useAbandonReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()

      if (redirectOnMutation) {
        window.location.href = redirectUrl
      } else {
        handleInlineSuccess(redirectUrl)
      }
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const handleReviewSubmit = useCallback(() => {
    if (submitDisabled) return

    if (errorMessage) setErrorMessage(undefined)
    setReviewSubmissionState('submitting')
    sendPullRequestAnalyticsEvent('submit_review_dialog.submit', 'SUBMIT_REVIEW_BUTTON')
    submitReview({
      body: reviewBodyRef.current,
      event: reviewEvent,
      headSha: headRefOid,
    })
  }, [
    errorMessage,
    headRefOid,
    reviewEvent,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
    submitDisabled,
    submitReview,
  ])

  const handleReviewCancel = useCallback(() => {
    if (!pendingCommentData?.pendingReviewIDs || pendingCommentData?.pendingReviewIDs.length === 0) return
    if (!confirm('Are you sure you want to cancel? You will lose all your pending comments.')) return

    if (errorMessage) setErrorMessage(undefined)

    setReviewSubmissionState('canceling')
    sendPullRequestAnalyticsEvent('submit_review_dialog.cancel', 'CANCEL_REVIEW_BUTTON')
    abandonReview()
  }, [
    abandonReview,
    errorMessage,
    pendingCommentData.pendingReviewIDs,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
  ])

  const reviewMenuButtonDisplayState = useMemo(
    () =>
      getReviewMenuButtonDisplayState({
        isPROpen,
        viewerAllowedNonCommentReviewTypes,
        viewerIsAuthor,
      }),
    [viewerAllowedNonCommentReviewTypes, viewerIsAuthor, isPROpen],
  )

  const handleOpenReviewDialog = useCallback(() => {
    // on narrow screens, the dialog is a better experience than the anchored overlay
    if (window.innerWidth < 768) {
      setOpenReviewMenu('dialog')
    } else {
      setOpenReviewMenu('anchored')
    }

    sendPullRequestAnalyticsEvent('submit_review_dialog.open', 'REVIEW_CHANGES_BUTTON')
  }, [sendPullRequestAnalyticsEvent, setOpenReviewMenu])

  const handleCloseReviewDialog = useCallback(() => {
    setOpenReviewMenu('none')
  }, [setOpenReviewMenu])

  const handleNavigateToDiffComment = useCallback(() => {
    setOpenReviewMenu('none')
  }, [setOpenReviewMenu])

  const handleToggleReviewDialog = useCallback(() => {
    if (openReviewMenu === 'none') {
      handleOpenReviewDialog()
    } else {
      handleCloseReviewDialog()
    }
  }, [handleCloseReviewDialog, handleOpenReviewDialog, openReviewMenu])

  const ReviewMenuBody = useMemo(
    () =>
      deferredDataEnabled ? (
        <ReviewMenuContentWithoutDiffEntries
          pendingReviewIds={pendingCommentData.pendingReviewIDs}
          initialReviewBody={initialReviewBody}
          isPROpen={isPROpen}
          onReviewBodyChange={onReviewBodyChange}
          reviewEvent={reviewEvent}
          viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
          viewerCanWriteToRepo={viewerCanWriteToRepo}
          viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
          viewerIsAuthor={viewerIsAuthor}
          viewerIsCopilotAttributed={viewerIsCopilotAttributed}
          handleNavigateToDiffComment={handleNavigateToDiffComment}
          handleReviewSubmit={handleReviewSubmit}
          handleReviewEventChange={handleReviewEventChange}
          stackBaseBranch={stackBaseBranch}
        />
      ) : (
        <ReviewMenuContent
          pendingReviewIds={pendingCommentData.pendingReviewIDs}
          initialReviewBody={initialReviewBody}
          isPROpen={isPROpen}
          onReviewBodyChange={onReviewBodyChange}
          reviewEvent={reviewEvent}
          viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
          viewerCanWriteToRepo={viewerCanWriteToRepo}
          viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
          viewerIsAuthor={viewerIsAuthor}
          viewerIsCopilotAttributed={viewerIsCopilotAttributed}
          handleNavigateToDiffComment={handleNavigateToDiffComment}
          handleReviewSubmit={handleReviewSubmit}
          handleReviewEventChange={handleReviewEventChange}
          stackBaseBranch={stackBaseBranch}
        />
      ),
    [
      deferredDataEnabled,
      pendingCommentData.pendingReviewIDs,
      initialReviewBody,
      isPROpen,
      onReviewBodyChange,
      reviewEvent,
      viewerAllowedNonCommentReviewTypes,
      viewerCanWriteToRepo,
      viewerHasViolatedPushPolicy,
      viewerIsAuthor,
      viewerIsCopilotAttributed,
      handleNavigateToDiffComment,
      handleReviewSubmit,
      handleReviewEventChange,
      stackBaseBranch,
    ],
  )

  if (reviewMenuButtonDisplayState.isHidden) return null

  const reviewMenuLoading = (
    <Stack align="center" justify="center" className="py-6">
      <Spinner size="medium" />
    </Stack>
  )

  const reviewMenuBody = isReviewMenuLoading ? reviewMenuLoading : ReviewMenuBody

  const reviewMenuFooter = (
    <ReviewMenuFooterComponent
      errorMessage={errorMessage}
      hasPendingReviews={hasPendingReviews}
      reviewSubmissionState={reviewSubmissionState}
      handleReviewCancel={handleReviewCancel}
      handleCloseReviewDialog={handleCloseReviewDialog}
      handleReviewSubmit={handleReviewSubmit}
      submitDisabled={submitDisabled}
      reviewMenuButtonDisplayState={reviewMenuButtonDisplayState}
    />
  )

  return (
    <>
      <Button
        count={totalPendingComments ? totalPendingComments : undefined}
        className={styles.ReviewMenuButton}
        trailingAction={TriangleDownIcon}
        variant="primary"
        onClick={handleToggleReviewDialog}
        size="small"
        ref={submitButtonRef}
      >
        <span className={styles.submitTextFull}>Submit {reviewMenuButtonDisplayState.text?.toLowerCase()}</span>
        <span className={styles.submitTextShort}>{reviewMenuButtonDisplayState.text}</span>
      </Button>

      {overlayPortal === 'internal' && <OverlayPortalContainer portalName={REVIEW_MENU_PORTAL_ROOT} />}

      <AnchoredOverlay
        open={openReviewMenu === 'anchored'}
        anchorRef={submitButtonRef}
        width="xlarge"
        onClose={handleCloseReviewDialog}
        displayCloseButton={false}
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        renderAnchor={null}
        focusZoneSettings={{disabled: true}}
        overlayProps={{
          role: 'dialog',
          'aria-labelledby': 'anchored-review-title',
          'aria-modal': 'true',
          ...(overlayPortal === 'internal' && {portalContainerName: REVIEW_MENU_PORTAL_ROOT}),
        }}
      >
        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          className="py-2 border-bottom tmp-pl-3 pr-2"
        >
          <Heading as="h1" className="flex-1 f5" variant="small" id="anchored-review-title">
            Finish your {reviewMenuButtonDisplayState.text?.toLowerCase()}
          </Heading>
          <Stack direction="horizontal" align="center" gap="none">
            {hasPendingReviews && (
              <IconButton
                icon={MaximizeIcon}
                className={styles.ExpandSection}
                variant="invisible"
                aria-label="Expand this section"
                onClick={() => setOpenReviewMenu('dialog')}
              />
            )}
            <IconButton icon={XIcon} variant="invisible" aria-label="Close" onClick={handleCloseReviewDialog} />
          </Stack>
        </Stack>

        <div className={styles.AnchoredReviewBody}>{reviewMenuBody}</div>

        <div className="tmp-p-3 border-top">{reviewMenuFooter}</div>
      </AnchoredOverlay>

      {openReviewMenu === 'dialog' && (
        <Dialog
          onClose={handleCloseReviewDialog}
          returnFocusRef={submitButtonRef}
          aria-label="Review changes"
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          title={`Finish your ${reviewMenuButtonDisplayState.text?.toLowerCase()}`}
          renderFooter={() => <Dialog.Footer>{reviewMenuFooter}</Dialog.Footer>}
        >
          {reviewMenuBody}
        </Dialog>
      )}
    </>
  )
}
/**
 * ControlledCommentBox is a wrapper around ConversationCommentBox that allows
 * it to be controlled by an initial value and an onChange callback.
 * This is useful for cases where you want to manage the comment box state
 * outside of the component to prevent unnecessary re-renders on every keystroke.
 */
const ControlledCommentBox = ({
  ref,
  initialValue,
  onChange,
  ...props
}: Omit<ConversationCommentBoxProps, 'value'> & {
  initialValue: string
  onChange: (value: string) => void
} & {ref?: React.Ref<CommentBoxHandle>}) => {
  const [reviewBody, setReviewBody] = useState<string>(initialValue)

  const onCommentBoxChange = useCallback(
    (value: string) => {
      setReviewBody(value)
      onChange(value)
    },
    [setReviewBody, onChange],
  )

  return <ConversationCommentBox {...props} value={reviewBody} onChange={onCommentBoxChange} ref={ref} />
}

ControlledCommentBox.displayName = 'ControlledCommentBox'

type ReviewMenuFooterProps = {
  errorMessage?: string
  handleReviewCancel: () => void
  handleCloseReviewDialog: () => void
  handleReviewSubmit: () => void
  hasPendingReviews: boolean
  submitDisabled?: boolean
  reviewMenuButtonDisplayState: {text?: string}
  reviewSubmissionState: 'idle' | 'submitting' | 'canceling'
}

function ReviewMenuFooterComponent({
  errorMessage,
  handleReviewCancel,
  handleCloseReviewDialog,
  handleReviewSubmit,
  hasPendingReviews,
  submitDisabled,
  reviewMenuButtonDisplayState,
  reviewSubmissionState,
}: ReviewMenuFooterProps) {
  const isCancelling = reviewSubmissionState === 'canceling'
  const isSubmitting = reviewSubmissionState === 'submitting'

  return (
    <>
      {errorMessage && (
        <Flash className={styles.ErrorMessage} variant="danger">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
      <div className="d-flex flex-row flex-1 flex-items-center flex-justify-between gap-2">
        {hasPendingReviews ? (
          <Button
            disabled={isCancelling || isSubmitting}
            loading={isCancelling}
            onClick={handleReviewCancel}
            tabIndex={0}
            variant="danger"
          >
            <Stack direction="horizontal" align="center">
              Discard {reviewMenuButtonDisplayState.text?.toLowerCase()}
            </Stack>
          </Button>
        ) : (
          <div />
        )}
        <div className="d-flex flex-row gap-2">
          <Button onClick={handleCloseReviewDialog} disabled={isCancelling || isSubmitting}>
            Cancel
          </Button>
          <Button
            disabled={submitDisabled || isSubmitting}
            variant="primary"
            onClick={handleReviewSubmit}
            loading={isSubmitting}
            className={styles.SubmitReviewButton}
            trailingVisual={<KeybindingHint keys="Mod+enter" />}
          >
            <div className="d-flex flex-row flex-justify-center">
              Submit {reviewMenuButtonDisplayState.text?.toLowerCase()}
            </div>
          </Button>
        </div>
      </div>
    </>
  )
}

interface ReviewMenuContentProps {
  initialReviewBody: string
  isPROpen: boolean
  onReviewBodyChange: (body: string) => void
  pendingReviewIds?: number[]
  reviewEvent: ReviewEvent
  viewerAllowedNonCommentReviewTypes: AllowedNonCommentReviewType[]
  viewerCanWriteToRepo: boolean
  viewerHasViolatedPushPolicy?: boolean | null
  viewerIsAuthor: boolean
  viewerIsCopilotAttributed: boolean
  handleNavigateToDiffComment: () => void
  handleReviewSubmit: () => void
  handleReviewEventChange: (newReviewEvent: string | null) => void
  stackBaseBranch?: string
}

/**
 * Shared component that renders the review menu content (comment box, type selector, comments section)
 */
function ReviewMenuContent({
  initialReviewBody,
  isPROpen,
  onReviewBodyChange,
  reviewEvent,
  pendingReviewIds,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  handleNavigateToDiffComment,
  handleReviewSubmit,
  handleReviewEventChange,
  stackBaseBranch,
}: ReviewMenuContentProps) {
  const markdownInputRef = useRef<CommentBoxHandle>(null)
  const commentsList = useGetPendingCommentsFromStore(pendingReviewIds)

  useEffect(() => {
    const timeout = window.setTimeout(() => markdownInputRef.current?.focus())
    return () => {
      window.clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <ControlledCommentBox
        ref={markdownInputRef}
        label="Add review comment"
        placeholder="Leave a comment"
        className={styles.CommentBoxContainer}
        initialValue={initialReviewBody}
        onChange={onReviewBodyChange}
        onPrimaryAction={handleReviewSubmit}
      />

      <ReviewTypeSelector
        isPROpen={isPROpen}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        handleReviewEventChange={handleReviewEventChange}
        handleReviewSubmit={handleReviewSubmit}
        stackBaseBranch={stackBaseBranch}
      />

      <ReviewCommentsSection
        totalPendingComments={commentsList.length}
        commentsList={commentsList}
        handleNavigateToDiffComment={handleNavigateToDiffComment}
      />
    </>
  )
}

function ReviewMenuContentWithoutDiffEntries({
  initialReviewBody,
  isPROpen,
  onReviewBodyChange,
  reviewEvent,
  pendingReviewIds,
  viewerAllowedNonCommentReviewTypes,
  viewerCanWriteToRepo,
  viewerHasViolatedPushPolicy,
  viewerIsAuthor,
  viewerIsCopilotAttributed,
  handleNavigateToDiffComment,
  handleReviewSubmit,
  handleReviewEventChange,
  stackBaseBranch,
}: ReviewMenuContentProps) {
  const markdownInputRef = useRef<CommentBoxHandle>(null)
  const commentsList = useGetPendingCommentsFromStoreWithoutDiffEntries(pendingReviewIds)

  useEffect(() => {
    const timeout = window.setTimeout(() => markdownInputRef.current?.focus())
    return () => {
      window.clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <ControlledCommentBox
        ref={markdownInputRef}
        label="Add review comment"
        placeholder="Leave a comment"
        className={styles.CommentBoxContainer}
        initialValue={initialReviewBody}
        onChange={onReviewBodyChange}
        onPrimaryAction={handleReviewSubmit}
      />

      <ReviewTypeSelector
        isPROpen={isPROpen}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        handleReviewEventChange={handleReviewEventChange}
        handleReviewSubmit={handleReviewSubmit}
        stackBaseBranch={stackBaseBranch}
      />

      <ReviewCommentsSection
        totalPendingComments={commentsList.length}
        commentsList={commentsList}
        handleNavigateToDiffComment={handleNavigateToDiffComment}
      />
    </>
  )
}
